import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DrawIcon from '@mui/icons-material/Draw';
import HandymanIcon from '@mui/icons-material/Handyman';
import { statsApi } from './api';
import { COLORS, formatMoney, getFirstOfMonth, getToday } from './utils';
import NavBar from './components/NavBar';
import Filters from './components/Filters';
import SummaryCards from './components/SummaryCards';
import ContractsChart from './components/ContractsChart';
import StatusPieChart from './components/StatusPieChart';
import RevenueChart from './components/RevenueChart';
import SalesmanBarChart from './components/SalesmanBarChart';
import SalesmanTable from './components/SalesmanTable';

const Dashboard = ({ onLogout, page, onPageChange }) => {
  const [proposalType, setProposalType] = useState('');
  const [dateFrom, setDateFrom] = useState(getFirstOfMonth());
  const [dateTo, setDateTo] = useState(getToday());
  const [groupBy, setGroupBy] = useState('day');
  const [summary, setSummary] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (proposalType) params.proposalType = proposalType;
    if (dateFrom) params.createdFrom = dateFrom;
    if (dateTo) params.createdTo = dateTo;

    try {
      const [summaryRes, salesmenRes, timelineRes] = await Promise.all([
        statsApi.getSummary(params),
        statsApi.getBySalesman(params),
        statsApi.getTimeline({ ...params, groupBy }),
      ]);
      setSummary(summaryRes.data);
      setSalesmen(salesmenRes.data);
      setTimeline(timelineRes.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }, [proposalType, dateFrom, dateTo, groupBy]);

  useEffect(() => { loadData(); }, [loadData]);

  const statusData = useMemo(() =>
    summary?.byStatus
      ? Object.entries(summary.byStatus).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] }))
      : [],
    [summary]
  );

  const totalRevenue = Number(summary?.revenue || 0);
  const totalEquipment = Number(summary?.equipmentTotal || 0);
  const totalPaid = Number(summary?.totalPaid || 0);
  const paidEquipment = Number(summary?.paidEquipment || 0);
  const netEarnings = totalPaid - paidEquipment;
  const conversionRate = summary?.total ? ((summary.signed / summary.total) * 100).toFixed(1) : '0';
  const collectionRate = totalRevenue > 0 ? ((totalPaid / totalRevenue) * 100).toFixed(0) : '0';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <NavBar page={page} onPageChange={onPageChange} onLogout={onLogout} onRefresh={loadData} loading={loading} />

      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        <Filters
          proposalType={proposalType} setProposalType={setProposalType}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          groupBy={groupBy} setGroupBy={setGroupBy}
          loading={loading} onApply={loadData}
        />

        {loading && !summary ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <SummaryCards
              summary={summary}
              totalRevenue={totalRevenue}
              totalEquipment={totalEquipment}
              totalPaid={totalPaid}
              paidEquipment={paidEquipment}
              netEarnings={netEarnings}
              conversionRate={conversionRate}
              collectionRate={collectionRate}
            />
            <ContractsChart data={timeline} />
            <StatusPieChart data={statusData} />
            <RevenueChart data={timeline} />
            <SalesmanBarChart
              title="Salesmen by Paid-Equip"
              icon={<PaidIcon sx={{ color: '#00695c', fontSize: 24 }} />}
              data={salesmen}
              valueFn={(row) => Number(row.paid || 0) - Number(row.paidEquipment || 0)}
              color="#00695c"
              formatter={formatMoney}
            />
            <SalesmanBarChart
              title="Salesmen by Installation Completed"
              icon={<CheckCircleIcon sx={{ color: '#1565c0', fontSize: 24 }} />}
              data={salesmen}
              dataKey="installCompleted"
              color="#1565c0"
            />
            <SalesmanBarChart
              title="Salesmen by Installation Signed"
              icon={<DrawIcon sx={{ color: '#4caf50', fontSize: 24 }} />}
              data={salesmen}
              dataKey="installSigned"
              color="#4caf50"
            />
            <SalesmanBarChart
              title="Salesmen by Service Signed"
              icon={<HandymanIcon sx={{ color: '#7b1fa2', fontSize: 24 }} />}
              data={salesmen}
              dataKey="serviceSigned"
              color="#7b1fa2"
            />
            <SalesmanBarChart
              title="Salesmen by Service Paid"
              icon={<PaidIcon sx={{ color: '#9c27b0', fontSize: 24 }} />}
              data={salesmen}
              dataKey="servicePaid"
              color="#9c27b0"
              formatter={formatMoney}
            />
            <SalesmanTable
              salesmen={salesmen}
              summary={summary}
              conversionRate={conversionRate}
              totalRevenue={totalRevenue}
              totalEquipment={totalEquipment}
              totalPaid={totalPaid}
              netEarnings={netEarnings}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
